import React, {
  useCallback, useContext, useState, useEffect, useRef,
} from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { ChartBarIcon } from '@vlabs/icons';
import { FullFrameWithDetections } from '@vlabs/monokit/shared/ui/full-frame-with-detections';
import { Badge, Tooltip } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button/IconButton';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import st from './QueueCard.module.sass';
import { HLSPlayer } from '../hls-player/HLSPlayer';
import { QueueContext, QUEUE_CARD_MODES } from '../queue-context/QueueContext';
import { QueueEventsContext } from '../queue-context/QueueEventsContext';
import { QueueCardStatus } from './status-button/QueueCardStatus';

export const CountTypesToSymbol = {
  count_gte: '≥',
  count_lte: '≤',
};

const CountTypes = {
  GTE: 'count_gte',
  LTE: 'count_lte',
};

function getBadgeKind(count, countType, countThreshold) {
  if (!Number.isInteger(count)) return 'neutral';

  const isGteFail = countType === CountTypes.GTE && count >= countThreshold;
  const isLteFail = countType === CountTypes.LTE && count <= countThreshold;

  return isGteFail || isLteFail ? 'negative' : 'positive';
}

const getToken = async () => {
  const { data: { token } } = await apiContainer.lunaClient.tokens.create({
    payload: {
      expiration_time: null,
      permissions: {
        streams: ['view', 'creation', 'modification', 'deletion'],
      },
    },
    query: {
      grant_all_permissions: 1,
    },
    config: {
      auth: {
        username: 'root@example.com',
        password: 'root',
      },
    },
  });

  return token;
};

export function QueueCard({ stream }) {
  // URL и токен для доступа к трансляции с камеры
  const [retranslation, setRetranslation] = useState(null);
  // Флаг, указывающий, обрезано ли имя потока
  // Используется для отображения тултипа с полным именем потока
  const [isStreamNameTruncated, setIsStreamNameTruncated] = useState(false);
  const textRef = useRef(null);
  const {
    queueCardSettings,
    setActiveTab,
    setStreamIdsForStatistic,
  } = useContext(QueueContext);
  const { events } = useContext(QueueEventsContext);
  const { t } = useTranslation();
  const history = useHistory();

  const {
    status,
    name: streamName,
    stream_id: streamId,
    description,
  } = stream || {};

  const handleClickEditStream = () => {
    history.push(`/queue/edit/${streamId}`);
  };

  const handleClickStatistic = () => {
    setStreamIdsForStatistic([streamId]);
    setActiveTab('statistic');
  };

  const getRetranslation = useCallback(async () => {
    try {
      const accessToken = await getToken();
      const {
        url,
        token: retransmissionToken,
      } = await apiContainer.lunaClient.streams.getRetransmission(streamId, accessToken);
      setRetranslation({ url, token: retransmissionToken });
    } catch {
      toast.error(t('queue:queue card.errors.retranslation error'));
    }
  }, [streamId, t]);

  useEffect(() => {
    if (queueCardSettings.mode === QUEUE_CARD_MODES.RETRANSLATION) {
      getRetranslation();
    }
  }, [getRetranslation, queueCardSettings.mode]);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      // Проверяем, обрезан ли текст в названии потока
      setIsStreamNameTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [streamName]);

  const {
    zone_count_type: countType,
    zone_count_threshold: countThreshold,
  } = JSON.parse(description);

  const currentEvent = events[streamId];

  return (
    <div className={st.QueueCardWrapper}>
      <div className={st.FrameSection}>
        {
          !currentEvent && queueCardSettings.mode === QUEUE_CARD_MODES.LAST_FRAME && (
            <img
              alt="No event placeholder"
              className={st.NoEventPlaceholder}
              src="/QueueCardPlaceholder.png"
            />
          )
        }
        {
          !!currentEvent?.overview?.image && queueCardSettings.mode === QUEUE_CARD_MODES.LAST_FRAME && (
            <FullFrameWithDetections
              detections={{
                type: 'point',
                coordinates: currentEvent?.overview?.people_coordinates,
                color: queueCardSettings.showDotsOnFaces.color,
                dotSize: queueCardSettings.showDotsOnFaces.size,
              }}
              src={currentEvent?.overview?.image}
              withDetections={queueCardSettings.showDotsOnFaces.enabled}
            />
          )
        }
        {
          queueCardSettings.mode === QUEUE_CARD_MODES.RETRANSLATION && !!retranslation && (
            <HLSPlayer
              className={st.Player}
              src={retranslation.url}
              token={retranslation?.token}
            />
          )
        }
      </div>
      <div className={st.InfoSection}>
        <div className={st.Info}>
          <Tooltip
            position="bottom"
            tooltipContent={isStreamNameTruncated ? streamName : null}
          >
            <div
              className={st.StreamName}
              ref={textRef}
            >
              {streamName}
            </div>
          </Tooltip>
          <div className={st.State}>
            <span className={st.CountType}>
              {CountTypesToSymbol[countType]}
              {' '}
              {countThreshold}
              {' '}
              {t('queue:queue card.info.ppl')}
            </span>
            <Badge
              kind={getBadgeKind(currentEvent?.people_count, countType, countThreshold)}
              size="large"
              value={Number.isInteger(currentEvent?.people_count) ? currentEvent?.people_count : '?'}
            />
          </div>
        </div>
        <div className={st.Actions}>
          <QueueCardStatus
            status={status}
            streamId={streamId}
          />
          <div className={st.Buttons}>
            <Button
              data-testid="QueueCard_editStreamButton"
              kind="secondary"
              onClick={handleClickEditStream}
              size="small"
              variant="outlined"
            >
              {t('queue:queue card.buttons.settings.label')}
            </Button>
            <Tooltip
              position="bottom"
              tooltipContent={t('queue:queue card.buttons.statistic.tooltip content')}
            >
              <IconButton
                data-testid="QueueCard_statisticButton"
                icon={<ChartBarIcon />}
                kind="secondary"
                onClick={handleClickStatistic}
                shape="square"
                size="small"
                variant="outlined"
              />
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
}
